import { useCallback } from 'react';
import { isEmpty } from 'lodash';
import type { FetchResult, MutationFunctionOptions } from '@apollo/client';
import type { ContactFieldsFragment, MutationErrorType } from '@/graphql/types';
import { createPaginatedRefetchOptions } from '../../../../utils/paginatedMutation';

type FieldErrors = Record<string, string>;

type MutationPayload = {
    contact?: ContactFieldsFragment | null;
    errors?: MutationErrorType[] | null;
};

type MutationSuccess = {
    success: true;
    contact: ContactFieldsFragment;
    mutationError: null;
    fieldErrors: null;
};

type MutationFailure = {
    success: false;
    contact: null;
    mutationError: string | null;
    fieldErrors: FieldErrors | null;
};

export type MutationExecutionResult = MutationSuccess | MutationFailure;

const REFETCH_OPTIONS = createPaginatedRefetchOptions('Contacts');

// Maps payload errors to a `FieldErrors` object and a general `errorMessage`.
// Returns `null` for empty values to simplify UI conditional rendering.
function parsePayloadErrors(errors: MutationErrorType[]) {
    const fieldErrorMap: FieldErrors = {};
    const globalErrors: string[] = [];

    errors.forEach((error) => {
        if (error.field) {
            fieldErrorMap[error.field] = error.message || gettext('INVALID');
        } else if (error.message) {
            globalErrors.push(error.message);
        }
    });

    return {
        fieldErrors: !isEmpty(fieldErrorMap) ? fieldErrorMap : null,
        errorMessage: !isEmpty(globalErrors) ? globalErrors.join(' ') : null,
    };
}

function createFailureResult(
    mutationError: string | null = null,
    fieldErrors: FieldErrors | null = null
): MutationFailure {
    return {
        success: false,
        contact: null,
        mutationError,
        fieldErrors,
    };
}

export function useContactMutationExecutor() {
    const executeMutation = useCallback(
        async <TData, TInput>(
            mutationFn: (options: MutationFunctionOptions<TData, TInput>) => Promise<FetchResult>,
            variables: TInput,
            payloadKey: string
        ): Promise<MutationExecutionResult> => {
            let result: MutationExecutionResult = {
                success: false,
                contact: null,
                mutationError: null,
                fieldErrors: null,
            };

            try {
                const response = await mutationFn({ variables, ...REFETCH_OPTIONS });
                const payload = response.data?.[payloadKey] as MutationPayload | null | undefined;

                // Handle Success.
                if (payload?.contact) {
                    result = {
                        success: true,
                        contact: payload.contact,
                        mutationError: null,
                        fieldErrors: null,
                    };
                }
                // Handle "Logical" Errors (API returns HTTP 200, but business logic failed).
                else if (payload?.errors && payload.errors.length > 0) {
                    const { errorMessage, fieldErrors } = parsePayloadErrors(payload.errors);
                    result = createFailureResult(errorMessage, fieldErrors);
                }
                // Handle "Silent Failure" (No Data & No Errors).
                else {
                    result = createFailureResult(gettext('ERROR_PAGE_BODY'));
                }
            } catch (error) {
                // Handle Network/System Exceptions.
                const message = error instanceof Error ? error.message : String(error);
                const safeMessage =
                    message === 'Internal server error' ? gettext('ERROR_PAGE_BODY') : message;
                result = createFailureResult(safeMessage);
            }

            return result;
        },
        []
    );

    return {
        executeMutation,
    };
}
